"use client";

import { useMemo, useState, useCallback, useEffect } from "react";
import { usePermissions } from "@/hooks/use-permissions";
import { formatDuration, median, percentile } from "./response-time-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Clock, AlertTriangle, Timer, RotateCcw, ArrowUpDown, HelpCircle, Trash2, Undo2, Loader2 } from "lucide-react";
import type { AnalyticsConfig, ResponseTimingRecord, ResponseType, RtExclusionRecord } from "../types";
import { subtractWeekendMs } from "@/lib/agents/email/analytics/business-time";
import { EmailViewerDialog } from "../../dialogs/email-viewer-dialog";

import type { EmailDetail } from "../../dialogs/email-viewer-dialog";

interface ResponseTimeSectionProps {
  data: ResponseTimingRecord[];
  agentId: string;
  config: AnalyticsConfig["response_time"];
  tz: string;
  degraded?: boolean;
  exclusions: RtExclusionRecord[];
  onExcludeMessage: (messageId: number) => Promise<void>;
  onExcludeSender: (senderEmail: string) => Promise<void>;
  onRestoreMessage: (messageId: number) => Promise<void>;
  onRestoreSender: (senderEmail: string) => Promise<void>;
  onResetExclusions: () => Promise<void>;
  onViewEmail?: (messageId: number) => Promise<EmailDetail | null>;
}

type SortField = "fromEmail" | "subject" | "responseType" | "responseTimeMs" | "receivedAt";
type SortDir = "asc" | "desc";
type DialogMode = "replied" | "pending" | null;

const RESPONSE_TYPE_LABELS: Record<ResponseType, string> = {
  agent_sent: "Agent Sent",
  escalated: "Escalated",
  forwarded: "Forwarded",
  human_replied: "Human Replied",
  sender_blacklisted: "Blacklisted",
  human_outbound: "Human Outbound",
  pending: "Pending",
};

export function ResponseTimeSection({
  data,
  agentId,
  config,
  tz,
  degraded,
  exclusions,
  onExcludeMessage,
  onExcludeSender,
  onRestoreMessage,
  onRestoreSender,
  onResetExclusions,
  onViewEmail,
}: ResponseTimeSectionProps) {
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [sortField, setSortField] = useState<SortField>("responseTimeMs");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Clean up stale localStorage keys from old implementation
  useEffect(() => {
    try {
      const key = `rt-exclusions-${agentId}`;
      if (localStorage.getItem(key) != null) {
        localStorage.removeItem(key);
      }
    } catch {
      // localStorage unavailable
    }
  }, [agentId]);

  // Build exclusion sets from DB records
  const { excludedMessageIds, excludedSenderEmails } = useMemo(() => {
    const msgIds = new Set<number>();
    const emails = new Set<string>();
    for (const ex of exclusions) {
      if (ex.exclusionType === "message" && ex.emailMessageId != null) {
        msgIds.add(ex.emailMessageId);
      }
      if (ex.exclusionType === "sender" && ex.senderEmail) {
        emails.add(ex.senderEmail.toLowerCase());
      }
    }
    return { excludedMessageIds: msgIds, excludedSenderEmails: emails };
  }, [exclusions]);

  const isExcluded = useCallback(
    (r: ResponseTimingRecord): boolean =>
      excludedMessageIds.has(r.messageId) ||
      (r.fromEmail != null && excludedSenderEmails.has(r.fromEmail.toLowerCase())),
    [excludedMessageIds, excludedSenderEmails],
  );

  // Find the reason a row is excluded (for undo UI)
  const getExclusionInfo = useCallback(
    (r: ResponseTimingRecord): { type: "message"; messageId: number } | { type: "sender"; senderEmail: string } | null => {
      if (excludedMessageIds.has(r.messageId)) {
        return { type: "message", messageId: r.messageId };
      }
      if (r.fromEmail != null && excludedSenderEmails.has(r.fromEmail.toLowerCase())) {
        return { type: "sender", senderEmail: r.fromEmail };
      }
      return null;
    },
    [excludedMessageIds, excludedSenderEmails],
  );

  const slaThresholdMs = config.sla_threshold_hours * 3_600_000;

  const stats = useMemo(() => {
    const included = data.filter((r) => !isExcluded(r));
    const respondedTimes = included
      .filter((r) => r.responseTimeMs != null)
      .map((r) => r.responseTimeMs!);

    const avg = respondedTimes.length > 0
      ? respondedTimes.reduce((s, v) => s + v, 0) / respondedTimes.length
      : 0;
    const med = median(respondedTimes);
    const p95 = percentile(respondedTimes, 95);

    const now = Date.now();

    const overSlaRepliedList = included.filter(
      (r) => r.responseTimeMs != null && r.responseTimeMs > slaThresholdMs,
    );

    const weekendDays = config.weekend_days ?? [];
    const overSlaPendingList = included.filter((r) => {
      if (r.responseType !== "pending" || !r.receivedAt) return false;
      const receivedTs = new Date(r.receivedAt).getTime();
      const waitMs = weekendDays.length > 0
        ? subtractWeekendMs(receivedTs, now, weekendDays, tz)
        : now - receivedTs;
      return waitMs > slaThresholdMs;
    });

    return {
      avg,
      med,
      p95,
      overSlaReplied: overSlaRepliedList,
      overSlaPending: overSlaPendingList,
      totalIncluded: included.length,
    };
  }, [data, isExcluded, slaThresholdMs, config.weekend_days, tz]);

  // Dialog emails: filtered and sorted
  const dialogEmails = useMemo(() => {
    const source = dialogMode === "replied"
      ? stats.overSlaReplied
      : dialogMode === "pending"
        ? stats.overSlaPending
        : [];

    return [...source].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "fromEmail":
          cmp = (a.fromEmail ?? "").localeCompare(b.fromEmail ?? "");
          break;
        case "subject":
          cmp = (a.subject ?? "").localeCompare(b.subject ?? "");
          break;
        case "responseType":
          cmp = a.responseType.localeCompare(b.responseType);
          break;
        case "responseTimeMs": {
          const now = Date.now();
          const aMs = a.responseTimeMs ?? (a.receivedAt ? now - new Date(a.receivedAt).getTime() : 0);
          const bMs = b.responseTimeMs ?? (b.receivedAt ? now - new Date(b.receivedAt).getTime() : 0);
          cmp = aMs - bMs;
          break;
        }
        case "receivedAt":
          cmp = (a.receivedAt ?? "").localeCompare(b.receivedAt ?? "");
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [dialogMode, stats.overSlaReplied, stats.overSlaPending, sortField, sortDir]);

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return field;
      }
      setSortDir("desc");
      return field;
    });
  }, []);

  const hasExclusions = exclusions.length > 0;

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Response Time</span>
              <span className="text-sm font-normal text-muted-foreground">
                (SLA: {config.sla_threshold_hours}h)
              </span>
            </CardTitle>
            {hasExclusions && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onResetExclusions}
                className="text-xs text-muted-foreground"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                <span>Reset exclusions (</span>{exclusions.length}<span>)</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {degraded && (
            <p className="text-xs text-orange-600 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span>Response time data may be incomplete due to a loading error. Metrics shown may not reflect actual response times.</span>
            </p>
          )}
          {/* Stats cards */}
          <TooltipProvider delayDuration={300}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatCard
                icon={<Timer className="w-4 h-4 text-blue-600" />}
                label="Average"
                value={formatDuration(stats.avg)}
                tooltip="Average response time across all responded emails in the selected period"
              />
              <StatCard
                icon={<Timer className="w-4 h-4 text-blue-600" />}
                label="Median"
                value={formatDuration(stats.med)}
                tooltip="Middle value — half of responses were faster, half were slower. Less affected by outliers than the average"
              />
              <StatCard
                icon={<Timer className="w-4 h-4 text-blue-600" />}
                label="P95"
                value={formatDuration(stats.p95)}
                tooltip="95th percentile — 95% of all responses were faster than this value. Shows worst-case response time excluding extreme outliers"
              />
              <StatCard
                icon={<AlertTriangle className="w-4 h-4 text-orange-600" />}
                label="Over SLA (replied)"
                value={`${stats.overSlaReplied.length} emails`}
                warn={stats.overSlaReplied.length > 0}
                onClick={stats.overSlaReplied.length > 0 ? () => setDialogMode("replied") : undefined}
                tooltip={`Emails that received a response but took longer than the ${config.sla_threshold_hours}h SLA threshold. Click to see details`}
              />
              <StatCard
                icon={<AlertTriangle className="w-4 h-4 text-red-600" />}
                label="Over SLA (pending)"
                value={`${stats.overSlaPending.length} emails`}
                warn={stats.overSlaPending.length > 0}
                onClick={stats.overSlaPending.length > 0 ? () => setDialogMode("pending") : undefined}
                tooltip={`Emails still waiting for a response beyond the ${config.sla_threshold_hours}h SLA threshold. Click to see details`}
              />
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Over SLA dialog — only mounts when opened to avoid preloading heavy table */}
      {dialogMode != null && (
        <OverSlaDialog
          mode={dialogMode}
          emails={dialogEmails}
          isExcluded={isExcluded}
          getExclusionInfo={getExclusionInfo}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
          onExcludeMessage={onExcludeMessage}
          onExcludeSender={onExcludeSender}
          onRestoreMessage={onRestoreMessage}
          onRestoreSender={onRestoreSender}
          onViewEmail={onViewEmail}
          onClose={() => setDialogMode(null)}
          tz={tz}
        />
      )}
    </>
  );
}

// ---- Sub-components --------------------------------------------------------

function StatCard({
  icon,
  label,
  value,
  warn,
  onClick,
  tooltip,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  warn?: boolean;
  onClick?: () => void;
  tooltip?: string;
}) {
  const clickable = onClick != null;
  const card = (
    <div
      className={`rounded-lg border p-3 ${warn ? "border-orange-200 bg-orange-50/50" : ""} ${clickable ? "cursor-pointer hover:bg-accent/50 transition-colors" : ""}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-xs text-muted-foreground">{label}</span>
        {tooltip && <HelpCircle className="w-3 h-3 text-muted-foreground/50" />}
      </div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );

  if (!tooltip) return card;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{card}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px] text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

function OverSlaDialog({
  mode,
  emails,
  isExcluded,
  getExclusionInfo,
  sortField,
  sortDir,
  onSort,
  onExcludeMessage,
  onExcludeSender,
  onRestoreMessage,
  onRestoreSender,
  onViewEmail,
  onClose,
  tz,
}: {
  mode: "replied" | "pending";
  emails: ResponseTimingRecord[];
  isExcluded: (r: ResponseTimingRecord) => boolean;
  getExclusionInfo: (r: ResponseTimingRecord) => { type: "message"; messageId: number } | { type: "sender"; senderEmail: string } | null;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
  onExcludeMessage: (messageId: number) => Promise<void>;
  onExcludeSender: (senderEmail: string) => Promise<void>;
  onRestoreMessage: (messageId: number) => Promise<void>;
  onRestoreSender: (senderEmail: string) => Promise<void>;
  onViewEmail?: (messageId: number) => Promise<EmailDetail | null>;
  onClose: () => void;
  tz?: string;
}) {
  const { can } = usePermissions();
  const [viewerEmail, setViewerEmail] = useState<EmailDetail | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [loadingMessageId, setLoadingMessageId] = useState<number | null>(null);

  const handleRowClick = useCallback(async (record: ResponseTimingRecord) => {
    if (!onViewEmail) return;
    setLoadingMessageId(record.messageId);
    try {
      const data = await onViewEmail(record.messageId);
      if (data) {
        setViewerEmail(data);
        setViewerOpen(true);
      }
    } finally {
      setLoadingMessageId(null);
    }
  }, [onViewEmail]);

  return (
    <>
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${mode === "pending" ? "text-red-600" : "text-orange-600"}`} />
            <span>{mode === "replied"
              ? `Over SLA — Replied (${emails.length})`
              : `Over SLA — No Response (${emails.length})`}</span>
          </DialogTitle>
        </DialogHeader>

        <TooltipProvider delayDuration={200}>
        <div className="overflow-auto flex-1 border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>
                  <SortButton field="fromEmail" label="From" current={sortField} dir={sortDir} onSort={onSort} />
                </TableHead>
                <TableHead>
                  <SortButton field="subject" label="Subject" current={sortField} dir={sortDir} onSort={onSort} />
                </TableHead>
                <TableHead>
                  <SortButton field="responseType" label="Type" current={sortField} dir={sortDir} onSort={onSort} />
                </TableHead>
                <TableHead>
                  <SortButton
                    field="responseTimeMs"
                    label={mode === "pending" ? "Waiting" : "Response Time"}
                    current={sortField}
                    dir={sortDir}
                    onSort={onSort}
                  />
                </TableHead>
                <TableHead>
                  <SortButton field="receivedAt" label="Received" current={sortField} dir={sortDir} onSort={onSort} />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emails.map((r) => {
                const excluded = isExcluded(r);
                const exclusionInfo = excluded ? getExclusionInfo(r) : null;
                const displayTime = r.responseType === "pending" && r.receivedAt
                  ? Date.now() - new Date(r.receivedAt).getTime()
                  : r.responseTimeMs;
                const isLoading = loadingMessageId === r.messageId;
                const clickable = !!onViewEmail && !excluded;
                return (
                  <TableRow
                    key={r.messageId}
                    className={`${excluded ? "opacity-40" : ""} ${clickable ? "cursor-pointer hover:bg-accent/50" : ""}`}
                    onClick={clickable ? () => handleRowClick(r) : undefined}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {excluded ? (
                        <RestoreButton
                          exclusionInfo={exclusionInfo}
                          onRestoreMessage={onRestoreMessage}
                          onRestoreSender={onRestoreSender}
                        />
                      ) : (
                        <ExcludeButton
                          fromEmail={r.fromEmail}
                          onExcludeMessage={() => onExcludeMessage(r.messageId)}
                          onExcludeSender={r.fromEmail ? () => onExcludeSender(r.fromEmail!) : undefined}
                        />
                      )}
                    </TableCell>
                    <TableCell className="max-w-[160px] text-sm">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block truncate">{r.fromEmail ?? "\u2014"}</span>
                        </TooltipTrigger>
                        {r.fromEmail && (
                          <TooltipContent side="top" className="max-w-[360px] break-all text-xs">
                            {r.fromEmail}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TableCell>
                    <TableCell className="max-w-[220px] text-sm">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block truncate">{r.subject ?? "\u2014"}</span>
                        </TooltipTrigger>
                        {r.subject && (
                          <TooltipContent side="top" className="max-w-[360px] break-words text-xs">
                            {r.subject}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${getTypeBadgeClass(r.responseType)}`}>
                        {RESPONSE_TYPE_LABELS[r.responseType]}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {formatDuration(displayTime)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
                      <span className="flex items-center gap-1.5">
                        {r.receivedAt
                          ? new Date(r.receivedAt).toLocaleString()
                          : "\u2014"}
                        {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emails.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                    No over-SLA emails
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>

    <EmailViewerDialog
      open={viewerOpen}
      onOpenChange={setViewerOpen}
      email={viewerEmail}
      userTimezone={tz}
      showCrmLink={can("crm.view")}
    />
    </>
  );
}

function SortButton({
  field,
  label,
  current,
  dir,
  onSort,
}: {
  field: SortField;
  label: string;
  current: SortField;
  dir: SortDir;
  onSort: (field: SortField) => void;
}) {
  return (
    <button
      className="flex items-center gap-1 hover:text-foreground"
      onClick={() => onSort(field)}
    >
      {label}
      <ArrowUpDown className={`w-3 h-3 ${current === field ? "text-foreground" : "text-muted-foreground/50"}`} />
      {current === field && (
        <span className="text-[10px]">{dir === "asc" ? "\u2191" : "\u2193"}</span>
      )}
    </button>
  );
}

function ExcludeButton({
  fromEmail,
  onExcludeMessage,
  onExcludeSender,
}: {
  fromEmail: string | null;
  onExcludeMessage: () => void;
  onExcludeSender?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" className="w-auto p-3" align="center">
        <p className="text-sm font-medium mb-2">Exclude from statistics</p>
        <div className="flex flex-col gap-1.5">
          <Button
            size="sm"
            variant="destructive"
            className="h-7 text-xs justify-start"
            onClick={() => { onExcludeMessage(); setOpen(false); }}
          >
            Only this message
          </Button>
          {onExcludeSender && fromEmail && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs justify-start text-destructive hover:text-destructive"
              onClick={() => { onExcludeSender(); setOpen(false); }}
            >
              <span>All from</span> {fromEmail.length > 30 ? `${fromEmail.slice(0, 30)}...` : fromEmail}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RestoreButton({
  exclusionInfo,
  onRestoreMessage,
  onRestoreSender,
}: {
  exclusionInfo: { type: "message"; messageId: number } | { type: "sender"; senderEmail: string } | null;
  onRestoreMessage: (messageId: number) => Promise<void>;
  onRestoreSender: (senderEmail: string) => Promise<void>;
}) {
  if (!exclusionInfo) return null;

  const tooltipText = exclusionInfo.type === "message"
    ? "Restore to statistics"
    : `Restore all from ${exclusionInfo.senderEmail}`;

  const handleClick = () => {
    if (exclusionInfo.type === "message") {
      onRestoreMessage(exclusionInfo.messageId);
    } else {
      onRestoreSender(exclusionInfo.senderEmail);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          onClick={handleClick}
        >
          <Undo2 className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="text-xs">
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}

function getTypeBadgeClass(type: ResponseType): string {
  switch (type) {
    case "agent_sent":
      return "bg-green-100 text-green-700";
    case "escalated":
      return "bg-orange-100 text-orange-700";
    case "forwarded":
      return "bg-purple-100 text-purple-700";
    case "human_replied":
      return "bg-blue-100 text-blue-700";
    case "sender_blacklisted":
      return "bg-gray-100 text-gray-700";
    case "human_outbound":
      return "bg-cyan-100 text-cyan-700";
    case "pending":
      return "bg-yellow-100 text-yellow-700";
  }
}
